import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { apiJson, apiVoid } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import type {
  TeamDto,
  TeamDetailDto,
  CreateTeamRequest,
  AddTeamMembersRequest,
  AddTeamMembersResponse,
} from '@signalvote/shared';

export function useTeams() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.teams.list(),
    queryFn: () => apiJson<TeamDto[]>('/api/teams', { error: 'Failed to fetch teams' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

export function useTeam(teamId: string | undefined) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.teams.detail(teamId ?? ''),
    queryFn: () => apiJson<TeamDetailDto>(`/api/teams/${teamId}`, { error: 'Failed to fetch team' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!teamId,
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTeamRequest) =>
      apiJson<TeamDto>('/api/teams', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to create team'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) =>
      apiVoid(`/api/teams/${teamId}`, {
        method: 'DELETE',
        error: (body) => new Error(body.error || 'Failed to delete team'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.volunteers.all });
    },
  });
}

export function useAddTeamMembers(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddTeamMembersRequest) =>
      apiJson<AddTeamMembersResponse>(`/api/teams/${teamId}/members`, {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to add team members'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.volunteers.all });
    },
  });
}

export function useRemoveTeamMember(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      apiVoid(`/api/teams/${teamId}/members/${userId}`, {
        method: 'DELETE',
        error: (body) => new Error(body.error || 'Failed to remove team member'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.volunteers.all });
    },
  });
}

/**
 * Assign users to multiple teams at once (bulk operation).
 * Sequentially adds the same set of users to each specified team.
 */
export function useBulkAssignToTeams() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamIds, userIds }: { teamIds: string[]; userIds: string[] }) => {
      for (const teamId of teamIds) {
        await apiVoid(`/api/teams/${teamId}/members`, {
          method: 'POST',
          json: { userIds },
          error: (body) => new Error(body.error || `Failed to assign members to team ${teamId}`),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.volunteers.all });
    },
  });
}
